import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, FileCheck2, FileX2, Loader2, ReceiptText } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { useFiscal } from "./FiscalGate";

/**
 * Estado das notas na tela de Pedidos: ícone no card e emissão manual.
 *
 * Busca o mapa `pedido_id -> nota` num endpoint separado, e não junto da
 * listagem de pedidos — aquela é cacheada e quente, e um JOIN ali penalizaria
 * inclusive as lojas sem o módulo fiscal.
 */

const API = "/api";
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const OK = new Set(["authorized", "inContingent"]);

const NotasCtx = createContext(null);

export function NotasFiscaisProvider({ children }) {
  const { ativo } = useFiscal();
  const [notas, setNotas] = useState({});
  const [pendentes, setPendentes] = useState(() => new Set());
  const [configurada, setConfigurada] = useState(false);
  const [confirmar, setConfirmar] = useState(null);
  const [emitindo, setEmitindo] = useState(false);

  const carregar = useCallback(async () => {
    if (!ativo) return;
    try {
      const { data } = await axios.get(`${API}/fiscal/notas/status`, auth());
      setNotas(data?.notas || {});
      setPendentes(new Set(data?.pendentes || []));
      setConfigurada(!!data?.emissao_configurada);
    } catch {
      // Sem assinatura ou falha: cards seguem sem indicador fiscal.
      setNotas({});
      setPendentes(new Set());
    }
  }, [ativo]);

  useEffect(() => { carregar(); }, [carregar]);

  const emitir = useCallback(async (pedido) => {
    setEmitindo(true);
    try {
      const { data } = await axios.post(`${API}/fiscal/emitir/${pedido.id}`, {}, auth());
      toast.success(
        data?.ja_emitida
          ? "Este pedido já tinha nota emitida."
          : "Nota enviada. A autorização chega em alguns segundos.",
      );
      await carregar();
      setConfirmar(null);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object") {
        // 422 traz a lista do que falta no cadastro fiscal — mostra tudo,
        // porque "erro ao emitir" não diz ao lojista o que fazer.
        toast.error(d.motivo || "Não foi possível emitir", {
          description: (d.pendencias || []).slice(0, 4).join(" · "),
          duration: 9000,
        });
      } else {
        toast.error(d || "Não foi possível emitir a nota");
      }
    } finally {
      setEmitindo(false);
    }
  }, [carregar]);

  const valor = useMemo(
    () => ({ notas, pendentes, configurada, ativo, recarregar: carregar, pedirEmissao: setConfirmar }),
    [notas, pendentes, configurada, ativo, carregar],
  );

  return (
    <NotasCtx.Provider value={valor}>
      {children}
      <AlertDialog open={!!confirmar} onOpenChange={(o) => !o && setConfirmar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Emitir nota fiscal?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  A NFC-e do pedido{" "}
                  <span className="font-semibold">#{String(confirmar?.codigo || "").replace("#", "")}</span>{" "}
                  será emitida no valor de{" "}
                  <span className="font-semibold">
                    R$ {Number(confirmar?.total || 0).toFixed(2).replace(".", ",")}
                  </span>.
                </p>
                <p className="text-muted-foreground text-[12.5px]">
                  Depois de autorizada, só dá para cancelar dentro de 28 minutos.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={emitindo}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); emitir(confirmar); }}
              disabled={emitindo}
            >
              {emitindo ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Emitindo...</>) : "Emitir nota"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </NotasCtx.Provider>
  );
}

export function useNotasFiscais() {
  return useContext(NotasCtx) || {
    notas: {}, pendentes: new Set(), configurada: false, ativo: false, recarregar: () => {}, pedirEmissao: () => {},
  };
}

/**
 * Selo de nota no card do pedido. Clicável quando NÃO há nota — é assim que o
 * lojista emite manualmente o que escapou da emissão automática.
 */
export function SeloNotaFiscal({ pedido, compacto = false }) {
  const { notas, pendentes, configurada, ativo, pedirEmissao } = useNotasFiscais();
  if (!ativo || !pedido?.id) return null;

  const nota = notas[pedido.id];
  const emitida = nota && OK.has(nota.status);
  // Cancelado com nota que continua valendo (fora dos 28 min) ainda mostra o selo.
  if ((pedido.status || "").toLowerCase() === "cancelado" && !(emitida && nota.aviso)) return null;

  if (emitida) {
    const teste = nota.ambiente && nota.ambiente !== "producao";
    const detalhe = nota.chave ? `NFC-e ${nota.numero || ""} · ${nota.chave}` : "Nota fiscal emitida";
    return (
      <span
        title={[teste ? "Nota de teste, sem valor fiscal" : "", detalhe, nota.aviso || ""].filter(Boolean).join("\n")}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
          nota.aviso
            ? "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
            : "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
        }`}
      >
        {nota.aviso ? <AlertTriangle className="w-3 h-3" /> : <FileCheck2 className="w-3 h-3" />}
        {compacto ? "NF" : teste ? "NF teste" : `NF ${nota.numero || "emitida"}`}
      </span>
    );
  }

  if (!nota && pendentes.has(pedido.id)) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); pedirEmissao(pedido); }}
        title="Fechado sozinho depois de 6 h, sem nota. Se foi venda de verdade, clique para emitir."
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold
                   bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 hover:bg-amber-200"
      >
        <AlertTriangle className="w-3 h-3" />
        {compacto ? "NF" : "Pendente de NF"}
      </button>
    );
  }

  // Loja que não configurou a emissão não vê "Emitir NF" em todo pedido
  // (o módulo é liberado pra todas desde 28/09/2026).
  if (!nota && !configurada) return null;

  const falhou = nota && !OK.has(nota.status);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); pedirEmissao(pedido); }}
      title={falhou ? "Nota não autorizada — clique para tentar de novo" : "Sem nota fiscal — clique para emitir"}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors ${
        falhou
          ? "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 hover:bg-red-200"
          : "bg-muted text-muted-foreground hover:bg-muted-foreground/20"
      }`}
    >
      {falhou ? <FileX2 className="w-3 h-3" /> : <ReceiptText className="w-3 h-3" />}
      {compacto ? "NF" : falhou ? "NF falhou" : "Emitir NF"}
    </button>
  );
}

/** Reimprime o cupom COM o bloco fiscal. Devolve true quando cuidou da impressão. */
export async function reimprimirComNota(pedidoId) {
  try {
    await axios.post(`${API}/fiscal/reimprimir/${pedidoId}`, {}, auth());
    toast.success("Cupom fiscal reimpresso!");
    return true;
  } catch (e) {
    if (e.response?.status === 404) return false;  // sem nota: segue o fluxo normal
    toast.error(e.response?.data?.detail || "Erro ao reimprimir a nota");
    return true;
  }
}
